import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { api } from '@/api/client'
import { trackFunnelPageView, trackSignUp } from '@/lib/analytics'

export default function SignupSuccessPage() {
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('session_id') ?? ''
  const navigate = useNavigate()
  const [status, setStatus] = useState<'waiting' | 'logging-in' | 'error'>('waiting')
  const [dots, setDots] = useState('.')

  // Animated dots
  useEffect(() => {
    const t = setInterval(() => setDots(d => d.length >= 3 ? '.' : d + '.'), 500)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    if (!sessionId) {
      navigate('/login')
      return
    }

    trackFunnelPageView()
    trackSignUp(sessionId)

    // Full page loads (not navigate) so the analytics script never stays
    // loaded once the user is inside the app.
    let attempts = 0
    const maxAttempts = 36 // ~3 minutes total, accounts for cold starts
    let cancelled = false

    const poll = async () => {
      if (cancelled) return

      try {
        const res = await api.get<{ isCompleted: boolean; tenantId: string | null; email: string | null }>(
          `/auth/signup/status?sessionId=${sessionId}`,
        )

        if (res.data.isCompleted && res.data.email) {
          setStatus('logging-in')
          window.location.replace('/login?signup=success')
          return
        }
      } catch {
        // continue polling
      }

      attempts++
      if (attempts >= maxAttempts) {
        // Account is very likely ready by now — send to login rather than showing an error
        window.location.replace('/login?signup=success')
        return
      }

      // Poll every 2s for first 10 attempts, then every 5s — fast for quick webhooks,
      // patient enough for cold starts
      setTimeout(poll, attempts < 10 ? 2000 : 5000)
    }

    poll()

    return () => {
      cancelled = true
    }
  }, [sessionId, navigate])

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center max-w-md px-6">
        {status === 'error' ? (
          <>
            <div className="text-4xl mb-4">⚠️</div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Taking longer than expected</h1>
            <p className="text-gray-500 text-sm mb-6">
              Your payment was received but setup is taking a moment. Please{' '}
              <a href="/login" className="text-indigo-600 font-medium">try signing in</a>{' '}
              — your account may already be ready. Contact us if you continue to have trouble.
            </p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              {status === 'logging-in' ? 'All set!' : `Setting up your account${dots}`}
            </h1>
            <p className="text-gray-500 text-sm">
              {status === 'logging-in'
                ? 'Redirecting you now…'
                : 'This usually takes just a few seconds.'}
            </p>
          </>
        )}
      </div>
    </div>
  )
}
